// Angular Imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// CC-Common imports
import { DxWidgetUtils, IconConstants, Utils } from 'cc-common';

// Third party imports
import { DxFormComponent } from 'devextreme-angular';

// Constant imports
import { Constants } from '../../../../_constants/constants';

// Service imports
import { PrecisionService } from '../../../../_services/shared-services/precision.service';

// Model imports
import { APServiceChargeEditorResultDto } from '../../_models/accounts-payable/service-charge/ap-service-charge-editor-result-dto';
import { APServiceChargeRawDto } from '../../_models/accounts-payable/service-charge/ap-service-charge-raw-dto';
import { DimensionSelectorResultDto } from '../../_models/common/dimensions/dimension-selector-result-dto';
import { GLAccountLabelSelectorResultDto } from '../../_models/common/gl-account/gl-account-label-selector-result-dto';
import { GLAccountSelectorResultDto } from '../../_models/common/gl-account/glaccount-selector-result-dto';
import { GLSubcategorySelectorResultDto } from '../../_models/common/gl-account/glsubcategory-selector-result-dto';
import { DimensionOptionCodeRawDto } from '../../_models/common/dimensions/dimension-option-code-raw-dto';

@Component({
    selector: 'app-service-charge-editor',
    templateUrl: './service-charge-editor.component.html',
    styleUrls: ['./service-charge-editor.component.scss']
})
export class ServiceChargeEditorComponent implements OnInit {
    @ViewChild('serviceChargeForm', { static: false }) serviceChargeForm: DxFormComponent;
    @Input() visible: boolean;
    @Input() serviceChargeEditorData: APServiceChargeEditorResultDto;
    @Input() popUpTitle: string;
    @Input() serviceChargeParam: any;
    @Input() subCategories: GLSubcategorySelectorResultDto[] = [];
    @Output() getSubcategories = new EventEmitter<any>();
    @Output() addServiceCharge = new EventEmitter<any>();
    @Output() closeServiceChargeEditor = new EventEmitter<boolean>();

    defaultLabelLocation: string;
    plusIcon: string = IconConstants.ADD_NEW;
    currencyFormat: any;

    serviceCharge: APServiceChargeRawDto;
    glDimensions: DimensionSelectorResultDto[] = [];
    glAccounts: GLAccountSelectorResultDto[] = [];
    glAccountLabels: GLAccountLabelSelectorResultDto[] = [];

    entityUsesSubCategory: boolean;

    accountLabelText = 'Account Label';
    glAccountText = 'GL Account';
    radioItems: string[] = [];
    selectedRadioItem: string;

    glAccountGridBoxValue: string[] = [];
    offSetGLAccountGridBoxValue: any[] = [];
    accountLabelGridBoxValue: any[] = [];
    glAccountColumns: any[] = [];
    offSetGLAccountColumns: any[] = [];
    accountLabelColumns: any[] = [];

    constructor(private readonly precisionService: PrecisionService) {
        this.currencyFormat = this.precisionService.getCurrencyFormat();
    }

    ngOnInit() {
        this.defaultLabelLocation = Constants.defaultFormLabelLocation;
        this.radioItems = [this.accountLabelText, this.glAccountText];
        this.selectedRadioItem = this.radioItems[1];
        if (Utils.notNullAndDefined(this.serviceChargeEditorData)) {
            this.entityUsesSubCategory = this.serviceChargeEditorData.entityUsesSubCategory;
            this.serviceCharge = this.serviceChargeEditorData.serviceCharge;
            this.glAccountGridBoxValue = Utils.notNullAndDefined(this.serviceCharge.glAccountNumber)
                ? [this.serviceCharge.glAccountNumber]
                : [];
            this.offSetGLAccountGridBoxValue = Utils.notNullAndDefined(this.serviceCharge.offSetGLAccountNumber)
                ? [this.serviceCharge.offSetGLAccountNumber]
                : [];
            this.accountLabelGridBoxValue = Utils.notNullAndDefined(this.serviceCharge.glAccountLabelCode)
                ? [this.serviceCharge.glAccountLabelCode]
                : [];
            this.glDimensions = this.serviceChargeEditorData.glDimensions;
            this.setDefaultDimensions();
            this.glAccounts = this.serviceChargeEditorData.glAccounts;
            this.glAccountLabels = this.serviceChargeEditorData.glAccountLabels;
            this.glAccountLabels.forEach((account) => {
                account.displayText = `${account.offsetGLAccountNumber}; ${account.offsetGLAccountTitle}`;
                account.labelDisplayText = `${account.title} - ${account.glAccountNumber} - ${account.offsetGLAccountTitle}`;
            });
            if (Utils.isTrue(this.entityUsesSubCategory)) {
                this.getSubcategories.emit(this.serviceCharge);
            }
            this.getGLAccountColumn();
        }
    }

    onHidden(): void {
        this.visible = false;
        this.closeServiceChargeEditor.emit(false);
    }

    onServiceChargeRadioClick(): void {
        if (Utils.notEqualsIgnoreCase(this.selectedRadioItem, 'Account Label')) {
            this.serviceCharge.glAccountLabelCode = null;
        } else {
            this.serviceCharge.glAccountNumber = null;
            this.serviceCharge.offSetGLAccountNumber = null;
        }
    }

    onGLAccountSelection(account: GLAccountSelectorResultDto[]): void {
        if (Utils.notNullOrEmpty(account)) {
            if (Utils.notEqualsIgnoreCase(this.serviceCharge.glAccountNumber, account[0].glAccountNumber)) {
                this.serviceCharge.subCategoryCode = null;
            }
            this.serviceCharge.glAccountNumber = account[0].glAccountNumber;

            setTimeout(() => {
                if (Utils.isTrue(this.entityUsesSubCategory)) {
                    this.getSubcategories.emit(this.serviceCharge);
                }
            });
        } else {
            this.serviceCharge.glAccountNumber = null;
        }
    }

    onOffsetGLAccountSelection(account: GLAccountLabelSelectorResultDto[]): void {
        if (Utils.notNullOrEmpty(account)) {
            this.serviceCharge.offSetGLAccountNumber = account[0].offsetGLAccountNumber;
        } else {
            this.serviceCharge.offSetGLAccountNumber = null;
        }
    }

    onAccountLabelSelection(label: GLAccountLabelSelectorResultDto[]): void {
        if (Utils.notNullOrEmpty(label)) {
            if (Utils.notEqualsIgnoreCase(this.serviceCharge.glAccountLabelCode, label[0].code)) {
                this.serviceCharge.subCategoryCode = null;
            }
            this.serviceCharge.glAccountLabelCode = label[0].code;
            this.serviceCharge.glAccountNumber = label[0].glAccountNumber;
            setTimeout(() => {
                if (Utils.isTrue(this.entityUsesSubCategory)) {
                    this.getSubcategories.emit(this.serviceCharge);
                }
            });
        } else {
            this.serviceCharge.glAccountLabelCode = null;
        }
    }

    onDimChangeClick(e: any, data: DimensionSelectorResultDto): void {
        if (Utils.notNullAndDefined(data)) {
            if (Utils.notNullAndDefined(e.value)) {
                const dimParamIndex = this.serviceCharge.dimensions.findIndex((obj) => {
                    return Utils.equalsIgnoreCase(obj.dimensionCode, data.code);
                });

                if (Utils.isNullOrUndefined(dimParamIndex)) {
                    const dimParam: any = {
                        dimensionCode: data.code,
                        dimensionOptionCode: e.value
                    };

                    this.serviceCharge.dimensions.push(dimParam);
                } else {
                    this.serviceCharge.dimensions[dimParamIndex].dimensionOptionCode = e.value;
                }
            } else {
                this.serviceCharge.dimensions = this.serviceCharge.dimensions.filter((obj) => {
                    return Utils.notEqualsIgnoreCase(obj.dimensionCode, data.code);
                });
            }
        }
    }

    onAddPayAndSChargeClick(): void {
        if (DxWidgetUtils.hasInstance(this.serviceChargeForm)) {
            const validator = this.serviceChargeForm.instance.validate().isValid;
            if (Utils.isTrue(validator)) {
                if (Utils.equalsIgnoreCase(this.selectedRadioItem, this.accountLabelText)) {
                    this.serviceCharge.glAccountNumber = null;
                }
                this.serviceCharge.payBill = false;
                this.addServiceCharge.emit(this.serviceCharge);
            }
        }
    }

    onAddSChargeAndPayIncludingBillClick(): void {
        if (DxWidgetUtils.hasInstance(this.serviceChargeForm)) {
            const validator = this.serviceChargeForm.instance.validate().isValid;
            if (Utils.isTrue(validator)) {
                if (Utils.equalsIgnoreCase(this.selectedRadioItem, this.accountLabelText)) {
                    this.serviceCharge.glAccountNumber = null;
                }
                this.serviceCharge.payBill = true;
                this.addServiceCharge.emit(this.serviceCharge);
            }
        }
    }

    get isSubCategoryDisabled(): boolean {
        return (
            Utils.isNullOrEmpty(this.subCategories) ||
            (Utils.equals(this.selectedRadioItem, this.accountLabelText) &&
                Utils.isNullOrUndefined(this.serviceCharge.glAccountLabelCode))
        );
    }

    private setDefaultDimensions(): void {
        if (Utils.notNullOrEmpty(this.serviceCharge.dimensions)) {
            this.glDimensions.forEach((option: DimensionSelectorResultDto) => {
                const dim = this.serviceCharge.dimensions.find((dimOption: DimensionOptionCodeRawDto) => {
                    return Utils.equalsIgnoreCase(dimOption.dimensionCode, option.code);
                });
                if (Utils.notNullAndDefined(dim)) {
                    option['selectedDimension'] = dim.dimensionOptionCode;
                }
            });
        }
    }

    private getGLAccountColumn(): void {
        this.glAccountColumns = [
            {
                dataField: 'glAccountNumber',
                dataType: 'string',
                caption: `${this.glAccountText} Number`
            },
            {
                dataField: 'title',
                dataType: 'string',
                caption: 'Title'
            },
            {
                dataField: 'displayText',
                dataType: 'string',
                caption: 'displayText',
                visible: false
            }
        ];

        this.offSetGLAccountColumns = [
            {
                dataField: 'offsetGLAccountNumber',
                dataType: 'string',
                caption: `Offset ${this.glAccountText} Number`
            },
            {
                dataField: 'offsetGLAccountTitle',
                dataType: 'string',
                caption: 'Title'
            },
            {
                dataField: 'displayText',
                dataType: 'string',
                caption: 'displayText',
                visible: false
            }
        ];

        this.accountLabelColumns = [
            {
                dataField: 'title',
                dataType: 'string',
                caption: this.accountLabelText
            },
            {
                dataField: 'glAccountNumber',
                dataType: 'string',
                caption: this.glAccountText
            },
            {
                dataField: 'offsetGLAccountNumber',
                dataType: 'string',
                caption: 'Offset Account'
            },
            {
                dataField: 'labelDisplayText',
                dataType: 'string',
                caption: 'displayText',
                visible: false
            }
        ];
    }
}
